import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faMapMarkerAlt, faPoundSign, faKey } from "@fortawesome/free-solid-svg-icons";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import {
  getNewBuildProperties,
  NewBuildPropertyItem,
} from "../utils/properties";
import { compareNewBuildListingOrder } from "../utils/newBuildSort";
import toCDNImage from "../utils/toCDNImage";
import placeholderImage from "../images/photo-coming-soon-300x300-1-square.jpg";

const formatPrice = (price?: string | number): string => {
  if (price === null || price === undefined || price === "") return "";
  const num = typeof price === "number" ? price : parseFloat(price);
  if (isNaN(num)) return "£" + price;
  return "£" + Math.round(num).toLocaleString("en-GB");
};

const priceToNumber = (price?: string | number): number => {
  if (!price) return 0;
  const num = typeof price === "number" ? price : parseFloat(price);
  return isNaN(num) ? 0 : num;
};

const UNIT_STATUS = {
  AVAILABLE: "available",
  SOLD: "sold",
  HELD: "held",
} as const;

function NewBuildCard({
  property,
  theme,
  onClick,
}: {
  property: NewBuildPropertyItem;
  theme: ThemeProps | undefined;
  onClick: () => void;
}) {
  const imageUrl = property.image
    ? toCDNImage(property.image, 600)
    : placeholderImage;

  const availableCount = property.units
    ? property.units.filter(
        (u) => u.status === UNIT_STATUS.AVAILABLE || u.status === "unknown" || !u.status
      ).length
    : 0;
  const totalUnits = property.no_of_units ?? property.units?.length ?? 0;

  const primaryColour = theme?.color1 || "#004AAE";

  return (
    <div
      className="new-build-card"
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <div className="new-build-card__image-wrap">
        <LazyLoadImage
          src={imageUrl}
          alt={property.title || property.developer || "New build property"}
          className="new-build-card__image"
          effect="opacity"
        />
        {totalUnits > 0 && (
          <span
            className="new-build-card__unit-badge"
            style={{ background: primaryColour }}
          >
            {availableCount} / {totalUnits} available
          </span>
        )}
      </div>
      <div className="new-build-card__body">
        <h3 className="new-build-card__title">
          {property.title || property.development || "New Build Development"}
        </h3>
        {property.developer && (
          <p className="new-build-card__developer">
            <FontAwesomeIcon icon={faBuilding} /> {property.developer}
          </p>
        )}
        {property.location && (
          <p className="new-build-card__location">
            <FontAwesomeIcon icon={faMapMarkerAlt} /> {property.location}
          </p>
        )}
        {(property.price_from || property.price_to) && (
          <p
            className="new-build-card__price"
            style={{ color: primaryColour }}
          >
            <FontAwesomeIcon icon={faPoundSign} />
            {property.price_from && ` From ${formatPrice(property.price_from)}`}
            {property.price_from && property.price_to && " – "}
            {property.price_to && `${formatPrice(property.price_to)}`}
          </p>
        )}
        {property.hook && (
          <p className="new-build-card__hook">{property.hook}</p>
        )}
      </div>
      <div
        className="new-build-card__footer"
        style={{ borderTopColor: primaryColour + "22" }}
      >
        <span
          className="new-build-card__cta"
          style={{ color: primaryColour }}
        >
          View Development →
        </span>
      </div>
    </div>
  );
}

function NewBuildListing() {
  const navigate = useNavigate();
  const theme = useThemeStore((state: { theme: ThemeProps | undefined }) => state.theme);
  const [allProperties, setAllProperties] = useState<NewBuildPropertyItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [region, setRegion] = useState("");
  const [town, setTown] = useState("");
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(999999999);
  const [activeSort, setActiveSort] = useState<"date" | "price">("date");

  const primaryColour = theme?.color1 || "#004AAE";
  const borderColor = theme?.color1 || "#004AAE";

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      const result = await getNewBuildProperties({ page: 1, limit: 100 });
      setAllProperties(result.results || []);
      setLoading(false);
    };
    fetchProperties();
  }, []);

  // Extract unique filter options from data
  const regions = useMemo(() => {
    const set = new Set<string>();
    allProperties.forEach((p) => {
      if (p.region) set.add(p.region);
    });
    return Array.from(set).sort();
  }, [allProperties]);

  const towns = useMemo(() => {
    const set = new Set<string>();
    allProperties.forEach((p) => {
      if (p.town) set.add(p.town);
      else if (p.location) set.add(p.location);
    });
    return Array.from(set).sort();
  }, [allProperties]);

  // Filter and sort
  const filteredProperties = useMemo(() => {
    let filtered = allProperties.filter((p) => {
      if (region && p.region !== region) return false;
      if (town) {
        const propTown = p.town || p.location || "";
        if (propTown !== town) return false;
      }
      const price = priceToNumber(p.price_from);
      if (price < minPrice || price > maxPrice) return false;
      return true;
    });

    if (activeSort === "price") {
      filtered.sort((a, b) => priceToNumber(a.price_from) - priceToNumber(b.price_from));
    } else {
      filtered.sort(compareNewBuildListingOrder);
    }

    return filtered;
  }, [allProperties, region, town, minPrice, maxPrice, activeSort]);

  const handleCardClick = (id: string) => {
    navigate(`/new-build/${id}`);
  };

  return (
    <>
      <Helmet>
        <title>New Build Properties</title>
      </Helmet>

      <div className="new-build-listing pt-20">
        <div className="pb-10 mt-28">
          <div className="border max-w-7xl m-auto p-5 rounded-xl shadow-md">
            <div className="flex border-b mb-4">
              <button
                className="px-6 py-3 text-sm font-semibold text-gray-500 hover:text-gray-700 border-b-2 border-transparent -mb-px"
                onClick={() => navigate('/listing')}
              >
                <FontAwesomeIcon icon={faKey} className="mr-2" />Off Market
              </button>
              <button
                className="px-6 py-3 text-sm font-semibold border-b-2 -mb-px"
                style={{ borderColor, color: borderColor }}
              >
                <FontAwesomeIcon icon={faBuilding} className="mr-2" />New Build
              </button>
            </div>
            <div className="grid lg:grid-cols-3 grid-cols-1 lg:gap-x-2 gap-1 max-w-7xl m-auto justify-center items-center lg:px-0 px-5">
              {/* Region Select */}
              <div className="px-2">
                <label
                  htmlFor="nb-select-region"
                  className="block text-base font-normal mb-2 text-primarycolor"
                >
                  Region
                </label>
                <div className="relative">
                  <select
                    id="nb-select-region"
                    className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                    style={{ borderColor }}
                    value={region}
                    onChange={(e) => { setRegion(e.target.value); setTown(""); }}
                  >
                    <option value="">All Regions</option>
                    {regions.map((r, i) => (
                      <option key={i} value={r}>{r}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Town Select */}
              <div className="px-2">
                <label
                  htmlFor="nb-select-town"
                  className="block text-base font-normal mb-2 text-primarycolor"
                >
                  Town
                </label>
                <div className="relative">
                  <select
                    id="nb-select-town"
                    className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                    style={{ borderColor }}
                    value={town}
                    onChange={(e) => { setTown(e.target.value); setRegion(""); }}
                  >
                    <option value="">All Towns</option>
                    {towns.map((t, i) => (
                      <option key={i} value={t}>{t}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Price Range Select */}
              <div className="px-2">
                <label
                  htmlFor="nb-select-price"
                  className="block text-base font-normal mb-2 text-primarycolor"
                >
                  Price
                </label>
                <div className="relative">
                  <select
                    id="nb-select-price"
                    className="py-3 px-4 pr-9 block w-full border-primarycolor border-2 rounded-md text-sm bg-white-f2f9fa appearance-none"
                    style={{ borderColor }}
                    value={`${minPrice}-${maxPrice}`}
                    onChange={(e) => {
                      const [min, max] = e.target.value.split("-").map(Number);
                      setMinPrice(min);
                      setMaxPrice(max);
                    }}
                  >
                    <option value="0-999999999">Any</option>
                    <option value="0-125000">Up to £125,000</option>
                    <option value="125000-250000">£125,000 - £250,000</option>
                    <option value="250000-500000">£250,000 - £500,000</option>
                    <option value="500000-1000000">£500,000 - £1,000,000</option>
                    <option value="1000000-5000000">£1,000,000 - £5,000,000</option>
                    <option value="5000000-999999999">£5,000,000+</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col md:flex-row items-center justify-between px-2">
              {/* Sort By */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 items-center">
                <h3 className="text-gray-700 font-medium col-span-2 md:col-span-1">Sort By:</h3>
                <button
                  onClick={() => setActiveSort("date")}
                  className="py-2 px-2 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                  style={{
                    backgroundColor: activeSort === "date" ? theme?.color2 || primaryColour : "white",
                    borderColor: activeSort === "date" ? theme?.color2 || primaryColour : "gray",
                    color: activeSort === "date" ? "white" : "black",
                  }}
                >
                  Newest
                </button>
                <button
                  onClick={() => setActiveSort("price")}
                  className="py-2 px-3 inline-flex justify-center items-center gap-2 rounded-md border font-medium shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-all text-sm"
                  style={{
                    backgroundColor: activeSort === "price" ? theme?.color2 || primaryColour : "white",
                    borderColor: activeSort === "price" ? theme?.color2 || primaryColour : "gray",
                    color: activeSort === "price" ? "white" : "black",
                  }}
                >
                  Lowest Price
                </button>
              </div>

              {/* Count */}
              <p className="text-sm text-gray-500 mt-3 md:mt-0">
                {filteredProperties.length} development{filteredProperties.length !== 1 ? "s" : ""} found
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="new-build-listing__content">
          {loading ? (
            <div className="new-build-listing__loading">
              <div className="new-build-listing__spinner" />
              <p>Loading developments…</p>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="new-build-listing__empty">
              <FontAwesomeIcon
                icon={faBuilding}
                className="new-build-listing__empty-icon"
              />
              <p>No new build properties match your filters.</p>
            </div>
          ) : (
            <div className="new-build-listing__grid">
              {filteredProperties.map((property) => (
                <NewBuildCard
                  key={property._id}
                  property={property}
                  theme={theme}
                  onClick={() => handleCardClick(property._id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .new-build-listing {
          min-height: 100vh;
          background: #f8fafc;
        }

        .new-build-listing__content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        .new-build-listing__loading,
        .new-build-listing__empty {
          text-align: center;
          padding: 80px 24px;
          color: #6b7280;
        }

        .new-build-listing__spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #004AAE;
          border-radius: 50%;
          animation: nb-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes nb-spin {
          to { transform: rotate(360deg); }
        }

        .new-build-listing__empty-icon {
          font-size: 3rem;
          margin-bottom: 12px;
          opacity: 0.3;
          display: block;
        }

        .new-build-listing__grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }

        /* Card */
        .new-build-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          overflow: hidden;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          display: flex;
          flex-direction: column;
        }

        .new-build-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.12);
        }

        .new-build-card__image-wrap {
          position: relative;
          height: 200px;
          background: #e2e8f0;
          overflow: hidden;
        }

        .new-build-card__image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .new-build-card__unit-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
        }

        .new-build-card__body {
          padding: 16px 18px 12px;
          flex: 1;
        }

        .new-build-card__title {
          font-size: 17px;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 8px;
          line-height: 1.3;
        }

        .new-build-card__developer,
        .new-build-card__location {
          font-size: 13px;
          color: #6b7280;
          margin: 0 0 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .new-build-card__price {
          font-size: 15px;
          font-weight: 700;
          margin: 8px 0 4px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .new-build-card__hook {
          font-size: 13px;
          color: #4b5563;
          margin: 6px 0 0;
          font-style: italic;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .new-build-card__footer {
          padding: 10px 18px;
          border-top: 1px solid;
          display: flex;
          justify-content: flex-end;
        }

        .new-build-card__cta {
          font-size: 13px;
          font-weight: 600;
        }

        @media (max-width: 600px) {
          .new-build-listing__grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
}

export default NewBuildListing;
